import { ConfigProvider, theme } from 'antd';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SetupPage } from './pages/SetupPage';
import { AppLayout } from './layouts/AppLayout';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { CellLinesPage } from './pages/CellLinesPage';
import { MaterialsPage } from './pages/MaterialsPage';
import { RequestsPage } from './pages/RequestsPage';
import { AuditPage } from './pages/AuditPage';
import { AdminPage } from './pages/AdminPage';
import { SamplesPage } from './pages/SamplesPage';
import { QualityPage } from './pages/QualityPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { StoragePage } from './pages/StoragePage';
import { TransactionsPage } from './pages/TransactionsPage';
import { OverviewPage } from './pages/OverviewPage';
import { WorkplacePage } from './pages/WorkplacePage';
import { AccountPage } from './pages/AccountPage';
import { SearchPage } from './pages/SearchPage';
import { HelpPage } from './pages/HelpPage';
import { CellLineCatalogAdminPage } from './pages/CellLineCatalogAdminPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { setTenantRuntimeSettings } from './lib/tenant';

function SessionGate() {
  const [state, setState] = useState<
    'loading' | 'anonymous' | 'ready' | 'change-password'
  >('loading');
  const location = useLocation();
  useEffect(() => {
    void fetch('/api/v1/auth/me', { credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) {
          setState('anonymous');
          return;
        }
        const user = (await response.json()) as {
          mustChangePassword: boolean;
          tenant?: {
            systemName?: string | null;
            systemNameZh?: string | null;
            systemNameEn?: string | null;
            timezone?: string | null;
          };
        };
        setTenantRuntimeSettings(user.tenant);
        setState(user.mustChangePassword ? 'change-password' : 'ready');
      })
      .catch(() => setState('anonymous'));
  }, [location.pathname]);
  if (state === 'loading') return <Spin fullscreen />;
  if (state === 'anonymous') return <Navigate to="/login" replace />;
  if (state === 'change-password' && location.pathname !== '/account')
    return <Navigate to="/account?tab=settings" replace />;
  return <Outlet />;
}

export function App() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'en-US' ? enUS : zhCN;

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          colorInfo: '#1677ff',
          borderRadius: 8,
          fontFamily:
            "Inter, 'Noto Sans SC', 'Microsoft YaHei', system-ui, sans-serif",
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route element={<SessionGate />}>
            <Route element={<AppLayout />}>
              <Route
                path="/"
                element={<Navigate to="/dashboard/workplace" replace />}
              />
              <Route path="/dashboard/workplace" element={<WorkplacePage />} />
              <Route path="/dashboard/board" element={<OverviewPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route
                path="/change-password"
                element={<Navigate to="/account?tab=settings" replace />}
              />
              <Route path="/cell-lines" element={<CellLinesPage />} />
              <Route
                path="/cell-line-catalog"
                element={<CellLineCatalogAdminPage />}
              />
              <Route path="/materials" element={<MaterialsPage />} />
              <Route path="/samples" element={<SamplesPage />} />
              <Route path="/storage" element={<StoragePage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/approvals" element={<RequestsPage approvals />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/users" element={<AdminPage view="users" />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route
                path="/admin"
                element={<Navigate to="/system" replace />}
              />
              <Route path="/quality" element={<QualityPage />} />
              <Route
                path="/system/quality"
                element={<Navigate to="/system?tab=quality" replace />}
              />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/system" element={<AdminPage view="system" />} />
              <Route path="/maintenance" element={<MaintenancePage />} />
              <Route
                path="/backups"
                element={<Navigate to="/maintenance?tab=backup" replace />}
              />
              <Route
                path="/system/maintenance"
                element={<Navigate to="/maintenance" replace />}
              />
              <Route
                path="/system/device-specs"
                element={<Navigate to="/system" replace />}
              />
              <Route
                path="/system/box-specs"
                element={<Navigate to="/system" replace />}
              />
              <Route path="/:section/*" element={<PlaceholderPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}
